import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Globe, FileSearch } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { Reveal } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import { MaturityBadge } from "@/components/MaturityBadge";
import { LEARNOVA } from "@/config/site";

const HERO_IMG = "/hero-collaboration.jpg";

export default function HomePage() {
  const { t } = useLanguage();
  useSEO({ title: t.home.seo.title, description: t.home.seo.description, path: "/" });
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);

  const h = t.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="container-site pt-14 sm:pt-20 lg:pt-28 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow"
            >
              {h.hero.eyebrow}
            </motion.p>
            <h1
              data-testid="hero-title"
              className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
            >
              <MaskedLines lines={h.hero.lines} />
            </h1>
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
            >
              {h.hero.sub}
            </motion.p>
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/innovation"
                data-testid="hero-cta-innovations"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98]"
              >
                {h.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/applications/learnova"
                data-testid="hero-cta-learnova"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
              >
                {h.hero.ctaSecondary}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-xl border border-border shadow-sm">
                <motion.img
                  src={HERO_IMG}
                  alt={h.hero.frameAlt}
                  style={{ y: imgY, objectPosition: "50% 55%" }}
                  className="h-72 sm:h-96 w-full object-cover scale-110"
                  loading="eager"
                />
              </div>
              <a
                href={LEARNOVA.prototypeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-prototype-badge"
                className="absolute -bottom-6 left-4 sm:-left-6 flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-ochre/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-ink font-display font-extrabold text-ochre text-sm" aria-hidden="true">
                  L
                </span>
                <span>
                  <span className="block font-display font-bold text-sm">{h.hero.badgeTitle}</span>
                  <span className="block text-xs text-muted-foreground">{h.hero.badgeSub}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-ochre" aria-hidden="true" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* EVOLUTION SPINE */}
      <section className="container-site py-16 sm:py-24 lg:py-32" aria-labelledby="evolution-title">
        <Reveal>
          <p className="eyebrow">{h.evolution.eyebrow}</p>
          <h2 id="evolution-title" className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            {h.evolution.title}
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{h.evolution.intro}</p>
        </Reveal>
        <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {h.evolution.steps.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08}>
              <li className="group relative h-full rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ochre/50">
                <span className="font-mono text-xs font-semibold text-ochre">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-lg font-bold tracking-tight">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* FEATURED INNOVATION — LEARNOVA */}
      <section className="bg-ink text-sand" aria-labelledby="featured-title">
        <div className="container-site py-16 sm:py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow">{h.featured.eyebrow}</p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <h2 id="featured-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                    {h.featured.name}
                  </h2>
                  <MaturityBadge status="prototype" onDark testId="learnova-badge-prototype" />
                </div>
                <p className="mt-3 text-sm font-mono uppercase tracking-[0.16em] text-ochre">{h.featured.category}</p>
                <p className="mt-5 font-display text-xl sm:text-2xl font-bold text-white">{h.featured.tagline}</p>
                <p className="mt-4 max-w-xl text-sand/75 leading-relaxed">{h.featured.description}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/applications/learnova"
                    data-testid="featured-cta-discover"
                    className="inline-flex items-center gap-2 rounded-full bg-ochre px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:bg-white hover:shadow-md active:scale-[0.98]"
                  >
                    {h.featured.ctaDiscover}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href={LEARNOVA.prototypeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="featured-cta-prototype"
                    className="inline-flex items-center gap-2 rounded-full border border-sand/30 px-6 py-3 text-sm font-semibold text-sand transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
                  >
                    {h.featured.ctaPrototype}
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {h.featured.principles.map((p) => (
                    <li
                      key={p}
                      className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-sand/90"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section className="container-site py-16 sm:py-24" aria-labelledby="portfolio-title">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="eyebrow">{h.portfolio.eyebrow}</p>
              <h2 id="portfolio-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
                {h.portfolio.title}
              </h2>
              <p className="mt-3 max-w-xl text-muted-foreground leading-relaxed">{h.portfolio.text}</p>
            </div>
            <Link
              to="/applications"
              data-testid="home-portfolio-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
            >
              {h.portfolio.cta}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* TRANSPARENCY */}
      <section className="bg-sand-muted/60 border-y border-border" aria-labelledby="transparency-title">
        <div className="container-site py-16 sm:py-24">
          <Reveal>
            <p className="eyebrow">{h.transparency.eyebrow}</p>
            <h2 id="transparency-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
              {h.transparency.title}
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[ShieldCheck, Globe, FileSearch].map((Icon, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full rounded-xl border border-border bg-card p-6 sm:p-8">
                  <Icon className="h-6 w-6 text-ochre" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-bold">{h.transparency.points[i].title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.transparency.points[i].text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="container-site py-16 sm:py-24">
        <Reveal>
          <div className="rounded-2xl border border-border bg-card p-8 sm:p-12 lg:p-16 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{h.ctaBand.title}</h2>
            <p className="mt-4 max-w-xl mx-auto text-muted-foreground">{h.ctaBand.text}</p>
            <Link
              to="/contact"
              data-testid="home-contact-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98]"
            >
              {h.ctaBand.cta}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
